import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { updateOKR } from 'components/RabbitOKRCreationPanel/OKRCreationPanelSlice';
import FeedbackComponent from 'components/RabbitOKRCreationPanel/components/FeedbackComponent';
import GradientCardV2 from 'components/RabbitOKRCreationPanel/components/GradientCardV2';
import {
  SECONDARY_PANEL_TYPES,
  closeTitleAndMetricsSuggestionPanel,
  openChangeDetailsPanel,
  openCreateOKRPanel,
} from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import useOKRConfig from 'components/hooks/useOKRConfig';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { cloneDeep } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import MeshMavenLoading from 'screens/performanceReview/components/ContinuousFeedback/Drawers/common/MeshMaven/MeshMavenLoading';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';
import { themeColors } from 'themes/clients/baseTheme';
import uuidv4 from 'utils/uuidv4';

import { Details, Refresh2, CollapseSidepanel } from 'assets/assetsV1/iconComponents';
import { MeshMavenWithHording } from 'assets/assetsV1/iconComponents/MeshMavenWithHording';

import { GOAL_MEASUREMENT_TYPES, OBJECTIVE_TYPES_MAP, OKR_PROGRESS_TYPES, OKR_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { isMobileView } from 'constants/isMobileView';
import {
  sendAIButtonClickedEvent,
  sendAISuggestionUsedEvent,
  sendConfigureMeshMavenClickedEvent,
  sendGeneratedMeshMavenPersonalizedGoalResults,
  sendReanalyzedMeshMavenResultsForGoals,
  sendUsedMeshMavenGeneratedContentEvent,
} from 'constants/mixpanelHelper';
import { GOALS_EVENT_SOURCES } from 'constants/trackingEvents';

import { Button } from 'uiToolkitV1/Button';
import { CarouselControl } from 'uiToolkitV1/CarouselControl';
import { Divider } from 'uiToolkitV1/Divider';
import GradientButton from 'uiToolkitV1/GradientButton/GradientButton';
import BetaTag from 'uiToolkitV1/Tag/BetaTag';
import { TextBodyMedium, TextHeader4 } from 'uiToolkitV1/Typography';

import { updateLocalStateValues } from '../../KRslice';
import { useUpdateKRMutation } from '../../datahooks';
import { useGenerateRecommendationForOKRMutation, useSaveFeedbackApiMutation } from '../../datahooks/useGeneratorAi';
import { AdvancedOptionsScreen, additionalInformationOptions } from './AdvancedOptionsScreen';
import { ErrorComponent } from './ErrorComponent';
import GoalMeshMavenAnalysis from './GoalMeshMavenAnalysis';

const Styled = styled(CollapseSidepanel)`
  color: #666666 !important;
`;

export default function TitleAndMetricsSuggestionPanel({
  secondaryPanelDetails,
  isOpenAsParent,
  isAlignmentDisabled = false,
}) {
  const dispatch = useDispatch();
  const editOKRDetailsFromRedux = useSelector(kRDrawerSelector);
  const { isOpenedFromEditOKRKebabMenu, isOpenedFromHeaderOKRSidePanel } = secondaryPanelDetails;
  const editOKRDetails = useMemo(() => {
    if (editOKRDetailsFromRedux?.localStateValues) {
      return { ...editOKRDetailsFromRedux, ...editOKRDetailsFromRedux?.localStateValues };
    }
    return { ...editOKRDetailsFromRedux };
  }, [editOKRDetailsFromRedux]);
  const okrDetailsFromRedux = useSelector(okrCreationPanelStateSelector);
  const newOKRDetails = secondaryPanelDetails?.okrDetails?.suggestedOKRDetails
    ? secondaryPanelDetails.okrDetails
    : okrDetailsFromRedux;
  const okrDetails = secondaryPanelDetails?.isNewOKR ? newOKRDetails : editOKRDetails;
  const { mutate: mutateEditOKR } = useUpdateKRMutation(editOKRDetails?.uuid);

  const { OBJECTIVE_TYPE } = useOKRConfig();
  const advanceOptionRef = useRef();
  const cardsContainerRef = useRef();
  const [genAIGroupId, setGenAIGroupId] = useState();

  const initialSuggestedDetails = okrDetails?.uuid
    ? editOKRDetailsFromRedux?.localStateValues?.suggestedOKRDetails
    : okrDetails?.suggestedOKRDetails;

  const [suggestedOKRDetails, setSuggestedOKRDetails] = useState(
    initialSuggestedDetails || [
      {
        title: '',
        measureType: '',
        measure: GOAL_MEASUREMENT_TYPES[1]?.code,
        target: 0,
        startingProgressValue: 0,
        toIds: okrDetails.toIds || [],
        requestId: null,
        isDefault: true,
        curFeedbackStatus: null,
        timeBoundDetails: null,
        parentPost: { uuid: null, text: 'None', type: OKR_TYPES.OBJECTIVE, visibilityType: 'PRIVATE' },
      },
      {
        title: '',
        measureType: '',
        measure: GOAL_MEASUREMENT_TYPES[1]?.code,
        target: 0,
        startingProgressValue: 0,
        toIds: okrDetails.toIds || [],
        requestId: null,
        isDefault: true,
        curFeedbackStatus: null,
        timeBoundDetails: null,
        parentPost: { uuid: null, text: 'None', type: OKR_TYPES.OBJECTIVE, visibilityType: 'PRIVATE' },
      },
      {
        title: '',
        measureType: '',
        measure: GOAL_MEASUREMENT_TYPES[1]?.code,
        target: 0,
        startingProgressValue: 0,
        toIds: okrDetails.toIds || [],
        requestId: null,
        isDefault: true,
        curFeedbackStatus: null,
        timeBoundDetails: null,
        parentPost: { uuid: null, text: 'None', type: OKR_TYPES.OBJECTIVE, visibilityType: 'PRIVATE' },
      },
    ]
  );

  const [currentCardIndex, setCurrentCardIndex] = useState(okrDetails?.currentCardIndex || 0);

  const isObjective = okrDetails?.type === OKR_TYPES.OBJECTIVE;
  const isDetailedObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.DETAILED.value;

  const showTrackingSection =
    (!isObjective || isDetailedObjectiveType) && okrDetails?.progressType === OKR_PROGRESS_TYPES.MANUAL;

  const isCompactObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.COMPACT.value;

  const showOwnersAndCollaborators = !isObjective || isCompactObjectiveType || isDetailedObjectiveType;

  const [isAdvanceOptionOpen, setAdvanceOptionOpen] = useState(false);
  const [advanceGenerationClicked, setAdvanceGenerationClicked] = useState(false);
  const { objectivesName, keyResultsName } = useOrganisationSettings();

  const [userPromptText, setUserPromptText] = useState();
  const [advanceSelectedOptions, setAdvanceSelectedOptions] = useState(
    additionalInformationOptions.filter((option) => option.isSelected === true)
  );

  const [isGenerating, setGenerating] = useState(false);
  const [errorNormalOptions, setErrorNormalOptions] = useState('');
  const [errorAdvanceOptions, setErrorAdvanceOptions] = useState('');
  const [isAdvanceResponseGenerating, setAdvanceResponseGenerating] = useState(false);

  const [formattedAdditionalInformationOptions, setFormattedAdditionalInformationOptions] = useState([]);

  useEffect(() => {
    if (okrDetails?.uuid && suggestedOKRDetails?.[0]?.requestId) {
      dispatch(updateLocalStateValues({ suggestedOKRDetails, currentCardIndex }));
    }
  }, [currentCardIndex, dispatch, okrDetails?.uuid, suggestedOKRDetails]);

  const handleTitleGeneration = useCallback(
    (recommendationsRes) => {
      const { recommendations = [], requestId = null } = recommendationsRes;
      setGenerating(false);
      setAdvanceResponseGenerating(false);

      if (recommendations && recommendations.length > 0) {
        const parentPost = okrDetails?.parentPost ?? {
          uuid: null,
          text: 'None',
          type: OKR_TYPES.OBJECTIVE,
          visibilityType: 'PRIVATE',
        };
        const newSuggestions = recommendations.map(({ title, tracking, explanation }, index) => ({
          title,
          recommendedTitle: title,
          recommendedTracking: showTrackingSection && tracking ? { ...tracking } : null,
          explanation,
          measureType: tracking?.measureType,
          measure: tracking?.measure,
          target: tracking?.target,
          startingProgressValue: tracking?.startingProgressValue,
          toIds: okrDetails.toIds || [],
          curFeedbackStatus: null,
          timeBoundDetails: okrDetails?.timeBoundDetails,
          requestId,
          parentPost: isAdvanceOptionOpen ? suggestedOKRDetails[currentCardIndex].parentPost : parentPost,
          revisionNumber: suggestedOKRDetails[0].isDefault ? index + 1 : suggestedOKRDetails.length + 1,
        }));

        const totalSuggestions = suggestedOKRDetails[0].isDefault
          ? [...newSuggestions]
          : [...suggestedOKRDetails, ...newSuggestions];

        if (isAdvanceOptionOpen) {
          setSuggestedOKRDetails(
            totalSuggestions.map((cardDetails, index) => {
              if (index === currentCardIndex) {
                return { ...cardDetails, revisionNumber: cardDetails.revisionNumber + 1 };
              }

              return { ...cardDetails };
            })
          );
        } else {
          setSuggestedOKRDetails(totalSuggestions);
        }

        if (totalSuggestions.length > 3) {
          setCurrentCardIndex(totalSuggestions.length - 1);
        }
        if (isAdvanceOptionOpen) {
          setErrorAdvanceOptions('');
          setAdvanceOptionOpen(false);
        } else {
          setErrorNormalOptions('');
        }
      }
    },
    [
      currentCardIndex,
      isAdvanceOptionOpen,
      okrDetails?.parentPost,
      okrDetails?.timeBoundDetails,
      okrDetails.toIds,
      showTrackingSection,
      suggestedOKRDetails,
    ]
  );

  const handleCancel = useCallback(() => {
    if (secondaryPanelDetails?.isOpenAsChild) {
      if (secondaryPanelDetails?.parentState?.type === SECONDARY_PANEL_TYPES.OPEN_CREATE_OKR_SIMPLE_PANEL) {
        dispatch(
          openCreateOKRPanel({
            okrDetails: { ...secondaryPanelDetails?.okrDetails },
            isSkipResetState: true,
          })
        );
      } else if (secondaryPanelDetails?.parentState?.type === SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL) {
        dispatch(openChangeDetailsPanel({}));
      }

      return;
    }
    dispatch(closeTitleAndMetricsSuggestionPanel());
  }, [
    dispatch,
    secondaryPanelDetails?.isOpenAsChild,
    secondaryPanelDetails?.okrDetails,
    secondaryPanelDetails?.parentState?.type,
  ]);

  const { mutate: generateTitle } = useGenerateRecommendationForOKRMutation({
    onUpdate: handleTitleGeneration,
    onError: ({ err }) => {
      setGenerating(false);
      setAdvanceResponseGenerating(false);
      if (isAdvanceOptionOpen) {
        setErrorAdvanceOptions(err);
        setAdvanceOptionOpen(false);
      } else {
        setErrorNormalOptions(err);
      }
    },
  });

  const { mutate: saveFeedback } = useSaveFeedbackApiMutation({
    onUpdate: () => {},
    onError: () => {
      const suggestedOKRDetailsClone = cloneDeep(suggestedOKRDetails);
      suggestedOKRDetailsClone[currentCardIndex].curFeedbackStatus = null;
      setSuggestedOKRDetails(suggestedOKRDetailsClone);
    },
  });

  const handleFeedbackSubmitted = useCallback(
    (feedback) => {
      const currentCardDetails = suggestedOKRDetails[currentCardIndex];
      const {
        requestId: curRequestId,
        recommendedTitle,
        recommendedTracking,
        title,
        revisionNumber,
        measureType,
        measure,
        target,
        startingProgressValue,
      } = currentCardDetails;

      if (curRequestId !== null) {
        const content = {};
        const isUseContent = feedback === 'USE';

        const serviceName = okrDetails?.type === OKR_TYPES.OBJECTIVE ? 'goalObjectiveGeneration' : 'goalKRGeneration';

        if (isUseContent) {
          content.recommendedContent = {
            title: recommendedTitle,
            trackingObject: showTrackingSection && recommendedTracking ? { ...recommendedTracking } : undefined,
          };
          content.usedContent = {
            title,
            trackingObject: showTrackingSection
              ? {
                  measureType,
                  measure,
                  target,
                  startingProgressValue,
                }
              : undefined,
          };
        }
        saveFeedback({
          payload: {
            userAction: feedback,
            requestId: curRequestId,
            ...content,
            serviceName,
            revisionNumber,
          },
        });
      }
    },
    [suggestedOKRDetails, currentCardIndex, okrDetails?.type, saveFeedback, showTrackingSection]
  );

  useEffect(() => {
    setGenAIGroupId(uuidv4());
  }, []);

  useEffect(() => {
    const isParentAligned = Boolean(okrDetails?.parentPost);
    setFormattedAdditionalInformationOptions(
      additionalInformationOptions.map((option) => {
        const modifiedOption = { ...option };
        if (modifiedOption.tagName === 'Title') {
          if (!okrDetails?.text || okrDetails?.text?.length < 10) {
            modifiedOption.isDisabled = true;
            modifiedOption.isSelected = false;
            modifiedOption.isNotDefined = true;
            if (Boolean(okrDetails?.text) && okrDetails?.text?.length < 10) {
              modifiedOption.disableReason = 'Title length is less than 10 characters';
            }
          }
        }
        if (modifiedOption.tagName === 'Description' && !okrDetails?.description) {
          modifiedOption.isDisabled = true;
          modifiedOption.isSelected = false;
        }
        if (modifiedOption.tagName === 'Alignment') {
          if (!isParentAligned) {
            modifiedOption.isDisabled = true;
            modifiedOption.isSelected = false;
            modifiedOption.isNotDefined = true;
          }
        }
        if (modifiedOption.tagName === 'Objective Level') {
          if (okrDetails.type === OKR_TYPES.KEY_RESULT) {
            modifiedOption.isSelected = false;
            modifiedOption.isDisabled = true;
            modifiedOption.subtitle = `The level of hierarchy at which the ${modifyString(
              keyResultsName,
              StringModifier.SENTENCE_CASE
            )} is being defined`;
          } else {
            modifiedOption.subtitle = `The level of hierarchy at which the ${modifyString(
              objectivesName,
              StringModifier.SENTENCE_CASE
            )} is being defined`;
          }
        }
        if (modifiedOption.tagName === 'Timeline') {
          if (okrDetails.type === OKR_TYPES.KEY_RESULT) {
            modifiedOption.subtitle += modifyString(keyResultsName, StringModifier.SENTENCE_CASE);
          } else {
            modifiedOption.subtitle += modifyString(objectivesName, StringModifier.SENTENCE_CASE);
          }
        }

        return modifiedOption;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editOKRDetails.parentPost,
    newOKRDetails.parentPost,
    secondaryPanelDetails.isNewOKR,
    keyResultsName,
    objectivesName,
  ]);

  useEffect(() => {
    setAdvanceSelectedOptions(formattedAdditionalInformationOptions.filter((option) => option.isSelected === true));
  }, [formattedAdditionalInformationOptions]);

  useEffect(() => {
    if (isAdvanceOptionOpen && advanceOptionRef.current) {
      advanceOptionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    } else {
      cardsContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [isAdvanceOptionOpen]);

  const callGenerateTitleApi = useCallback(
    (options = {}) => {
      const {
        parentPost = null,
        uuid = null,
        toIds,
        description,
        text,
        measure,
        measureType,
        target,
        startingProgressValue,
        type,
        timeBoundDetails,
        objectiveLevel,
        departmentId,
        departmentName,
        createdBy,
      } = okrDetails;

      const departmentDetails = objectiveLevel === 'DEPARTMENT' ? { departmentId, departmentName } : {};

      if (isAdvanceOptionOpen) {
        setAdvanceResponseGenerating(true);
      } else {
        setGenerating(true);
      }

      generateTitle({
        payload: {
          parentPost,
          uuid,
          toIds,
          description,
          text,
          measure,
          measureType,
          target,
          startingProgressValue,
          type,
          timeBoundDetails,
          objectiveLevel,
          createdBy,
          recommendationCount: 3,
          ...departmentDetails,
          groupId: genAIGroupId,
          enabledFeatures: { tracking: showTrackingSection, title: true },
          isTitle: true,
          ...options,
        },
      });
    },
    [okrDetails, isAdvanceOptionOpen, generateTitle, genAIGroupId, showTrackingSection]
  );

  useEffect(() => {
    if (formattedAdditionalInformationOptions.length === 0 || isOpenAsParent || okrDetails?.suggestedOKRDetails) {
      return;
    }
    callGenerateTitleApi({
      topicsSelected: formattedAdditionalInformationOptions
        .filter((option) => option.isSelected)
        .map((option) => option.value),
      revisionNumber: 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedAdditionalInformationOptions]);

  const handleGenerateButtonClick = useCallback(() => {
    setAdvanceGenerationClicked(true);
    setAdvanceResponseGenerating(true);
    const currentCardDetails = suggestedOKRDetails[currentCardIndex];
    callGenerateTitleApi({
      recommendationCount: 1,
      userPrompt: userPromptText,
      topicsSelected: advanceSelectedOptions.map((option) => option.value),
      revisionNumber: currentCardDetails.revisionNumber,
      text: currentCardDetails.title,
      measure: currentCardDetails.measure,
      measureType: currentCardDetails.measureType,
      target: currentCardDetails.target,
      startingProgressValue: currentCardDetails.startingProgressValue,
      timeBoundDetails: currentCardDetails.timeBoundDetails,
      parentPost: currentCardDetails.parentPost,
    });
  }, [advanceSelectedOptions, callGenerateTitleApi, currentCardIndex, suggestedOKRDetails, userPromptText]);

  const handleTryAgainClicked = useCallback(() => {
    setGenerating(true);
    callGenerateTitleApi({
      recommendationCount: 1,
      topicsSelected: advanceSelectedOptions.map((option) => option.value),
      revisionNumber: suggestedOKRDetails.length + 1,
    });
  }, [advanceSelectedOptions, callGenerateTitleApi, suggestedOKRDetails.length]);

  const handleChangeEditOKR = useCallback(
    (updates) => {
      mutateEditOKR({ updates });
    },
    [mutateEditOKR]
  );

  const updateTitleInPrimaryPanel = useCallback(() => {
    const {
      title,
      measure,
      target,
      startingProgressValue,
      measureType,
      parentPost,
      timeBoundDetails,
      toIds,
      realignApiPayload,
    } = suggestedOKRDetails[currentCardIndex];

    if (isOpenedFromEditOKRKebabMenu || isOpenedFromHeaderOKRSidePanel) {
      handleChangeEditOKR(
        showTrackingSection
          ? {
              text: title,
              measure,
              target,
              startingProgressValue,
              measureType,
              parentPost,
              timeBoundDetails,
              toIds,
            }
          : { text: title, parentPost, timeBoundDetails, toIds }
      );
      dispatch(closeTitleAndMetricsSuggestionPanel());

      return;
    }

    dispatch(
      secondaryPanelDetails?.parentState?.type === SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL
        ? updateLocalStateValues(
            showTrackingSection
              ? {
                  text: title,
                  measure,
                  target,
                  startingProgressValue,
                  measureType,
                  parentPost,
                  timeBoundDetails,
                  toIds,
                  realignApiPayload,
                }
              : { text: title, parentPost, timeBoundDetails, toIds, realignApiPayload }
          )
        : updateOKR(
            showTrackingSection
              ? {
                  text: title,
                  measure,
                  target,
                  startingProgressValue,
                  measureType,
                  parentPost,
                  timeBoundDetails,
                  toIds,
                }
              : { text: title, parentPost, timeBoundDetails, toIds }
          )
    );

    if (secondaryPanelDetails?.isOpenAsChild) {
      if (secondaryPanelDetails?.parentState?.type === SECONDARY_PANEL_TYPES.OPEN_CREATE_OKR_SIMPLE_PANEL) {
        dispatch(
          openCreateOKRPanel({
            okrDetails: {
              ...secondaryPanelDetails?.okrDetails,
              text: title,
              measure,
              target,
              startingProgressValue,
              measureType,
              parentPost,
              timeBoundDetails,
              toIds,
            },
            isSkipResetState: true,
          })
        );
      } else if (secondaryPanelDetails?.parentState?.type === SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL) {
        dispatch(openChangeDetailsPanel({}));
      }
    }
    if (!secondaryPanelDetails?.isOpenAsChild) {
      dispatch(closeTitleAndMetricsSuggestionPanel());
    }
  }, [
    suggestedOKRDetails,
    currentCardIndex,
    isOpenedFromEditOKRKebabMenu,
    isOpenedFromHeaderOKRSidePanel,
    dispatch,
    secondaryPanelDetails?.parentState?.type,
    secondaryPanelDetails?.isOpenAsChild,
    secondaryPanelDetails?.okrDetails,
    showTrackingSection,
    handleChangeEditOKR,
  ]);

  return (
    <Flex backgroundColor="#FFFFFF" flexDirection="column" justifyContent="space-between" h="100%">
      <Flex h="44px" alignItems="center" justifyContent="space-between" py={1} px="20px">
        <Flex alignItems="center">
          <MeshMavenWithHording boxSize="32px" />
          <TextHeader4 bold color={themeColors.text.g1} ml="10px">
            Maven
          </TextHeader4>
          <BetaTag ml={2} />
        </Flex>
        <Flex alignItems="center">
          <Button
            Styled
            StartIcon={Styled}
            variant="tertiary"
            style={{ height: '32px' }}
            onClick={() => handleCancel()}
          />
        </Flex>
      </Flex>
      {errorNormalOptions || errorAdvanceOptions ? (
        <Flex
          padding="12px 8px"
          paddingTop="8px"
          borderRadius="0px 8px 8px 0px"
          background="#FFF"
          flex={1}
          alignItems="flex-start"
          height="calc(100% - 133px)"
          overflow="scroll"
        >
          <ErrorComponent error={errorNormalOptions || errorAdvanceOptions} />
        </Flex>
      ) : (
        <Flex
          padding="12px 8px"
          paddingTop="8px"
          borderRadius="0px 8px 8px 0px"
          background="#FFF"
          flex={1}
          alignItems="flex-start"
          height="calc(100% - 133px)"
          overflow="scroll"
        >
          <Flex
            backgroundColor="#FFFFFF"
            w="100%"
            flexDirection="column"
            alignItems="flex-start"
            p=" 0px 12px 12px 12px"
            borderRadius="8px"
            border="1px solid #EBEFF3"
            background="#FDFDFD"
          >
            <Flex
              flexDirection="column"
              alignItems="flex-start"
              alignSelf="stretch"
              opacity={isAdvanceOptionOpen ? 0.4 : 1}
              pointerEvents={isAdvanceOptionOpen || isOpenAsParent ? 'none' : 'unset'}
            >
              <Box display="flex" padding="8px 0px" alignItems="center" alignSelf="stretch">
                <Box display="flex" flexDir="column" padding="8px 4px" alignItems="flex-start" flex="1 0 0">
                  <TextHeader4 bold>
                    {`Select your preferred ${modifyString(
                      isObjective ? objectivesName : keyResultsName,
                      StringModifier.SENTENCE_CASE
                    )}`}
                  </TextHeader4>
                  <TextBodyMedium fontSize="12px" color="#666">
                    Navigate through the cards to discover more options
                  </TextBodyMedium>
                </Box>
                <Flex>
                  <GradientButton
                    text="Try Again"
                    icon={Refresh2}
                    onClick={() => {
                      sendAIButtonClickedEvent({
                        eventProperties: {
                          source: okrDetails?.uuid
                            ? GOALS_EVENT_SOURCES.EDIT_GOAL_PANEL
                            : GOALS_EVENT_SOURCES.ADD_GOAL_PANEL,
                          type: GOALS_EVENT_SOURCES.AI.type.TRY_AGAIN,
                          goalId: okrDetails?.uuid || 'NA',
                          purpose: okrDetails?.uuid
                            ? GOALS_EVENT_SOURCES.AI.purpose.Goal_Editing
                            : GOALS_EVENT_SOURCES.AI.purpose.Goal_Creation,
                        },
                      });
                      sendReanalyzedMeshMavenResultsForGoals();
                      handleTryAgainClicked();
                    }}
                  />
                </Flex>
              </Box>
            </Flex>
            <Flex
              flexDirection="column"
              alignItems="flex-start"
              alignSelf="stretch"
              opacity={isAdvanceOptionOpen ? 0.4 : 1}
              pointerEvents={isAdvanceOptionOpen || isOpenAsParent ? 'none' : 'unset'}
              ref={cardsContainerRef}
              marginBottom="12px"
            >
              {isGenerating || isAdvanceResponseGenerating ? (
                <MeshMavenLoading
                  loadingText={
                    isMobileView()
                      ? `Generating recommendations 
                          via Maven`
                      : 'Generating recommendations via Maven'
                  }
                  textProps={{ whiteSpace: 'pre-line' }}
                  width="100%"
                />
              ) : (
                <GradientCardV2
                  suggestedOKRDetails={suggestedOKRDetails[currentCardIndex]}
                  onChange={(newSuggestions) => {
                    setSuggestedOKRDetails((prev) => {
                      const newSuggestedOKRDetails = [...prev];
                      newSuggestedOKRDetails[currentCardIndex] = newSuggestions;
                      return newSuggestedOKRDetails;
                    });
                  }}
                  showOwnersAndCollaborators={showOwnersAndCollaborators}
                  showTrackingSection={showTrackingSection}
                  allSuggestedOKRDetails={suggestedOKRDetails}
                  currentCardIndex={currentCardIndex}
                  isAlignmentDisabled={isAlignmentDisabled}
                  okrDetails={okrDetails}
                  isNewOKR={secondaryPanelDetails?.isNewOKR}
                />
              )}
            </Flex>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              alignSelf="stretch"
              opacity={isAdvanceOptionOpen ? 0.4 : 1}
              pointerEvents={isAdvanceOptionOpen || isOpenAsParent ? 'none' : 'unset'}
            >
              <Flex display="flex" padding="2px 4px" alignItems="flex-start">
                <CarouselControl
                  noOfTabs={suggestedOKRDetails.length}
                  selectedIndex={currentCardIndex}
                  onChange={(index) => {
                    setCurrentCardIndex(index);
                  }}
                />
              </Flex>
              <Button
                variant="tertiary"
                colorScheme="blue"
                onClick={() => {
                  sendConfigureMeshMavenClickedEvent({
                    eventProperties: {
                      source: okrDetails?.uuid
                        ? GOALS_EVENT_SOURCES.EDIT_GOAL_PANEL
                        : GOALS_EVENT_SOURCES.ADD_GOAL_PANEL,
                      type: showTrackingSection
                        ? `${GOALS_EVENT_SOURCES.CONFIGURE_AI.type.Parameters_Change} ,${GOALS_EVENT_SOURCES.CONFIGURE_AI.type.Title_Change}`
                        : GOALS_EVENT_SOURCES.CONFIGURE_AI.type.Title_Change,
                      goalId: okrDetails?.uuid,
                      purpose: okrDetails?.uuid
                        ? GOALS_EVENT_SOURCES.AI.purpose.Goal_Editing
                        : GOALS_EVENT_SOURCES.AI.purpose.Goal_Creation,
                    },
                  });
                  sendGeneratedMeshMavenPersonalizedGoalResults();
                  setAdvanceOptionOpen(true);
                }}
                padding="0px!important"
                ml="-8px!important"
              >
                Configure Maven for customized results
              </Button>
            </Flex>
            <Divider borderColor="#EBEFF3" width="100%" my="12px" />

            {suggestedOKRDetails?.[currentCardIndex]?.explanation && (
              <GoalMeshMavenAnalysis
                suggestedOKRDetails={suggestedOKRDetails[currentCardIndex]}
                isObjective={isObjective}
                isLoading={isGenerating || isAdvanceResponseGenerating}
                opacity={isAdvanceOptionOpen ? 0.4 : 1}
                pointerEvents={isAdvanceOptionOpen || isOpenAsParent ? 'none' : 'unset'}
              />
            )}

            {isAdvanceOptionOpen && (
              <Box marginTop="12px">
                <AdvancedOptionsScreen
                  advanceSelectedOptions={advanceSelectedOptions}
                  setAdvanceSelectedOptions={setAdvanceSelectedOptions}
                  advanceGenerationClicked={advanceGenerationClicked}
                  setAdvanceGenerationClicked={setAdvanceGenerationClicked}
                  formattedAdditionalInformationOptions={formattedAdditionalInformationOptions}
                  userPromptText={userPromptText}
                  setUserPromptText={setUserPromptText}
                  handleGenerateButtonClick={handleGenerateButtonClick}
                  isAdvanceResponseGenerating={isAdvanceResponseGenerating}
                  okrDetails={secondaryPanelDetails?.isNewOKR ? newOKRDetails : editOKRDetails}
                  isNewOKR={secondaryPanelDetails?.isNewOKR}
                  isShowCancelButton
                  onCancel={() => setAdvanceOptionOpen(false)}
                  ref={advanceOptionRef}
                />
              </Box>
            )}
            {isAdvanceOptionOpen && <Divider borderColor="#EBEFF3" width="100%" />}
            <Flex
              opacity={isAdvanceOptionOpen ? 0.4 : 1}
              pointerEvents={isAdvanceOptionOpen || isOpenAsParent ? 'none' : 'unset'}
            >
              <FeedbackComponent
                curFeedbackStatus={suggestedOKRDetails?.[currentCardIndex]?.curFeedbackStatus}
                onChange={(feedback) => {
                  setSuggestedOKRDetails((prev) =>
                    prev.map((cardDetails, index) => {
                      if (index === currentCardIndex) {
                        return { ...cardDetails, curFeedbackStatus: feedback };
                      }

                      return { ...cardDetails };
                    })
                  );

                  handleFeedbackSubmitted(feedback);
                }}
              />
            </Flex>
          </Flex>
        </Flex>
      )}

      <Flex
        flex="auto 0 0"
        borderTopWidth="1px"
        h="72px"
        justifyContent="space-between"
        alignItems="center"
        backgroundColor={themeColors.neutral.F5F7F9}
        px={3}
        pointerEvents={isOpenAsParent ? 'none' : 'unset'}
        opacity={isOpenAsParent ? 0.5 : 1}
      >
        <Button
          variant="tertiary"
          colorScheme="blue"
          onClick={() => {
            handleCancel();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="filled"
          colorScheme="blue"
          StartIcon={Details}
          startIconProps={{ fill: 'none' }}
          isDisabled={errorNormalOptions || errorAdvanceOptions || isGenerating || isAdvanceResponseGenerating}
          onClick={() => {
            sendAISuggestionUsedEvent({
              eventProperties: {
                goalId: okrDetails?.uuid || 'NA',
                purpose: okrDetails?.uuid
                  ? GOALS_EVENT_SOURCES.AI.purpose.Goal_Editing
                  : GOALS_EVENT_SOURCES.AI.purpose.Goal_Creation,
                source: 'Use these parameters',
              },
            });
            sendUsedMeshMavenGeneratedContentEvent({
              eventProperties: {
                source: okrDetails?.uuid ? 'Edit Goals' : 'Create Goals',
              },
            });
            updateTitleInPrimaryPanel();
            handleFeedbackSubmitted('USE');
          }}
        >
          Use these parameters{' '}
        </Button>
      </Flex>
    </Flex>
  );
}
